import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { SelectField } from '../../components/ui/SelectField';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api';
import {
  bulkClearConfirmation,
  bulkDeleteConfirmation,
  clientBulkDeleteConfirmation,
  summariseBulk,
  type BulkOperation,
  type BulkSummary,
} from '../../lib/portalAssets/bulk';
import { assetNameWithFile } from '../../lib/portalAssets/displayName';
import { RETENTION_DAYS } from '../../lib/portalAssets/deletion';
import { REVIEW_REASON_MAX } from '../../lib/portalAssets/review';
import type { PortalAssetTile } from '../../types/portalAsset';
import type { PortalAssetsBulkApi } from './assetsApi';

// THE BULK BAR (2026-10-13): approve, not approve (one reason for all), delete, add to a category, remove from a
// category, AI enrich, and (staff only) clear. SHARED since Run F: it names NO endpoint, each host passes a
// `PortalAssetsBulkApi` (staffApi.ts / the client's portalAssetsApi.ts), and endpoints.test.ts pins that.
//
// ⚠️ CLEAR IS OFFERED ONLY WHEN THE HOST SUPPLIES `api.clearPreview`. Clear is the one action with no recovery (no
// window, no restore: a title a person typed is gone), so a club's api has none and the button does not exist for it.
// The server refuses it for a club too (the client schema cannot carry it); this is the visible half.
// ⚠️ DELETE says what the host's user can do about it: a club is told it cannot restore and must ask Kyck.
//
// ⚠️⚠️ THE RESULT IS THE POINT. A bulk action that quietly does less than it says is the house defect at
// scale, so the outcome is a headline that always carries the count AND the total ("43 of 50 approved. 7
// failed.") and a list naming every asset that did not go through, with the reason. It stays on screen until
// dismissed. ⚠️ After a run the selection narrows to the ones that FAILED, so they can be looked at or retried.
//
// ⚠️ Only the VISIBLE selected tiles are sent (see useBulkSelection). Every rule — which assets can be reviewed,
// which categories fit which brand — is the SERVER's, per asset; this bar does not pre-judge, it reports.

type Mode = null | 'reject' | 'delete' | 'add' | 'remove' | 'enrich' | 'clear';

export function BulkActionBar({
  tiles,
  categories,
  categoryNote,
  onClear,
  onKeepOnly,
  onDone,
  selectNote,
  onSelectAll,
  api,
  audience,
}: {
  /** The VISIBLE selected tiles. */
  tiles: readonly PortalAssetTile[];
  /** The categories to offer, or null when none can be chosen (then `categoryNote` says why). */
  categories: ReadonlyArray<{ id: string; name: string }> | null;
  categoryNote: string | null;
  onClear: () => void;
  onKeepOnly: (ids: string[]) => void;
  /** Refetch the lists, the counts and any open detail. */
  onDone: () => void;
  selectNote: string | null;
  onSelectAll: () => void;
  /** The host's endpoints. */
  api: PortalAssetsBulkApi;
  /** Who is looking: decides the wording of the reject and delete sentences. */
  audience: 'staff' | 'client';
}) {
  const [mode, setMode] = useState<Mode>(null);
  const [reason, setReason] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [outcome, setOutcome] = useState<{ summary: BulkSummary; rejected: boolean } | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  // ⚠️ The title the person has been reading, with the original file after it when they differ (a failure line has no 'beneath').
  const nameOf = (id: string) => {
    const t = tiles.find((x) => x.id === id);
    return t ? assetNameWithFile(t) : id;
  };

  // CLEAR's exposure count: a READ fired by the click that opens the confirmation (a mutation, never an effect or query).
  const clearPreview = useMutation({ mutationFn: () => api.clearPreview!(tiles.map((t) => t.id)) });

  const run = useMutation({
    // AI enrichment (Run D) has its own route and its own concurrent loop; everything else is the sequential /bulk.
    mutationFn: (op: BulkOperation) =>
      (op.op === 'enrich'
        ? api.enrich(tiles.map((t) => t.id))
        : api.run(
            tiles.map((t) => t.id),
            op,
          )
      ).then((r) => ({ op, results: r.results })),
    onSuccess: ({ op, results }) => {
      setRequestError(null);
      const summary = summariseBulk(op, results, nameOf);
      setOutcome({ summary, rejected: summary.failed + summary.notAttempted > 0 });
      onKeepOnly(results.filter((r) => r.outcome !== 'ok').map((r) => r.assetId));
      setMode(null);
      setReason('');
      onDone();
    },
    // ⚠️ A request-level failure (offline, a 400 on the request itself) means NOTHING is known to have run:
    // say so in those words rather than guessing from a partial state.
    onError: (err) => {
      setOutcome(null);
      setRequestError(
        `${err instanceof ApiError ? err.message : 'The request failed.'} Nothing has been confirmed — check the grid before trying again.`,
      );
    },
  });

  const n = tiles.length;
  const approvedCount = tiles.filter((t) => t.review.status === 'approved').length;
  const busy = run.isPending;
  const categoryChosen = categories?.some((c) => c.id === categoryId) ? categoryId : '';
  const confirmDelete = (audience === 'client' ? clientBulkDeleteConfirmation : bulkDeleteConfirmation)(n, approvedCount, RETENTION_DAYS);
  const canClear = api.clearPreview !== undefined;
  const confirmClear = bulkClearConfirmation(n, clearPreview.data?.preview ?? null);

  return (
    <div className="sticky top-0 z-20 space-y-3 rounded-md border border-border bg-card p-3" role="region" aria-label="Bulk actions">
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-2 text-sm font-medium text-foreground">{n === 0 ? 'Nothing selected' : `${n} selected`}</p>
        {n === 0 ? (
          <Button size="sm" variant="outline" onClick={onSelectAll}>
            Select all on this page
          </Button>
        ) : (
          <>
            <Button size="sm" disabled={busy} onClick={() => run.mutate({ op: 'review', decision: 'approved' })}>
              {busy && mode === null ? 'Working…' : 'Approve'}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === 'reject' ? null : 'reject')}>
              Don't approve…
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === 'delete' ? null : 'delete')}>
              Delete…
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || categories === null}
              onClick={() => setMode(mode === 'add' ? null : 'add')}
            >
              Add to category…
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || categories === null}
              onClick={() => setMode(mode === 'remove' ? null : 'remove')}
            >
              Remove from category…
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === 'enrich' ? null : 'enrich')}>
              <Sparkles className="mr-1 h-4 w-4" aria-hidden />
              AI enrich…
            </Button>
            {canClear && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  const opening = mode !== 'clear';
                  setMode(opening ? 'clear' : null);
                  if (opening) clearPreview.mutate();
                }}
              >
                Clear title, tags &amp; categories…
              </Button>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={onClear}>
              Deselect
            </Button>
          </>
        )}
      </div>
      {selectNote && <p className="text-xs text-muted-foreground">{selectNote}</p>}
      {categories === null && categoryNote && n > 0 && <p className="text-xs text-muted-foreground">{categoryNote}</p>}

      {mode === 'reject' && n > 0 && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            run.mutate({ op: 'review', decision: 'rejected', reason });
          }}
        >
          <label htmlFor="bulk-reject-reason" className="block text-sm text-foreground">
            Why not?{' '}
            <span className="text-muted-foreground">
              One reason for all {n}. {audience === 'client' ? 'Kyck sees it.' : 'The clubs see it.'}
            </span>
          </label>
          <Textarea id="bulk-reject-reason" value={reason} maxLength={REVIEW_REASON_MAX} onChange={(e) => setReason(e.target.value)} />
          <Button size="sm" type="submit" disabled={busy || reason.trim() === ''}>
            {busy ? 'Working…' : `Don't approve ${n}`}
          </Button>
        </form>
      )}

      {mode === 'delete' && n > 0 && (
        <div className="space-y-2 text-sm" role="group" aria-label="Confirm bulk delete">
          <p className="font-medium text-foreground">{confirmDelete.title}</p>
          {confirmDelete.warning && <p className="font-medium text-foreground">{confirmDelete.warning}</p>}
          {confirmDelete.body.map((line) => (
            <p key={line} className="text-muted-foreground">
              {line}
            </p>
          ))}
          <Button size="sm" variant="destructive" disabled={busy} onClick={() => run.mutate({ op: 'delete' })}>
            {busy ? 'Deleting…' : `Delete ${n}`}
          </Button>
        </div>
      )}

      {mode === 'clear' && canClear && n > 0 && (
        <div className="space-y-2 text-sm" role="group" aria-label="Confirm clear" data-testid="bulk-clear-confirm">
          <p className="font-medium text-foreground">{confirmClear.title}</p>
          {clearPreview.isPending ? (
            <p className="text-muted-foreground">Checking what a person wrote…</p>
          ) : (
            <p className="font-medium text-foreground">{confirmClear.warning}</p>
          )}
          {confirmClear.body.map((line) => (
            <p key={line} className="text-muted-foreground">
              {line}
            </p>
          ))}
          <Button
            size="sm"
            variant={confirmClear.destructive ? 'destructive' : 'default'}
            disabled={busy || clearPreview.isPending}
            onClick={() => run.mutate({ op: 'clear' })}
          >
            {busy ? 'Clearing…' : `Clear ${n}`}
          </Button>
        </div>
      )}

      {mode === 'enrich' && n > 0 && (
        <div className="space-y-2 text-sm" role="group" aria-label="Confirm AI enrichment" data-testid="bulk-enrich-confirm">
          <p className="font-medium text-foreground">Suggest and save a title, tags and category for {n} selected?</p>
          <p className="text-muted-foreground">
            This sends each asset's preview image to an AI model, which costs a little per asset, and saves the result
            straight away (no accept step). It never overwrites a title, tags or category a person set. Large selections may
            run out of time; the ones not reached are listed and nothing is written for them.
          </p>
          <Button size="sm" disabled={busy} onClick={() => run.mutate({ op: 'enrich' })}>
            {busy ? 'Enriching…' : `Enrich ${n}`}
          </Button>
        </div>
      )}

      {(mode === 'add' || mode === 'remove') && categories !== null && n > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-64">
            <SelectField
              value={categoryChosen}
              onValueChange={setCategoryId}
              options={[{ value: '', label: 'Choose a category…' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
              aria-label="Category"
              size="sm"
            />
          </div>
          <Button
            size="sm"
            disabled={busy || categoryChosen === ''}
            onClick={() => run.mutate({ op: mode === 'add' ? 'addCategory' : 'removeCategory', categoryId: categoryChosen })}
          >
            {busy ? 'Working…' : mode === 'add' ? `Add ${n} to category` : `Remove ${n} from category`}
          </Button>
          {mode === 'add' && (
            <p className="basis-full text-xs text-muted-foreground">
              A category belongs to one brand: assets from another brand will be refused and listed, and the rest are
              added.
            </p>
          )}
        </div>
      )}

      {requestError && (
        <p className="text-sm text-destructive" role="alert">
          {requestError}
        </p>
      )}

      {outcome && (
        <div className="space-y-1 rounded-md border border-border p-3 text-sm" role="status" data-testid="bulk-result">
          <p className={outcome.rejected ? 'font-medium text-destructive' : 'font-medium text-foreground'}>{outcome.summary.headline}</p>
          {outcome.summary.notes.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground" data-testid="bulk-notes">
              {outcome.summary.notes.map((p) => (
                <li key={p} className="break-all">
                  {p}
                </li>
              ))}
            </ul>
          )}
          {outcome.summary.problems.length > 0 && (
            <>
              <p className="text-muted-foreground">These did not go through (they stay selected):</p>
              <ul className="list-disc space-y-0.5 pl-5 text-foreground">
                {outcome.summary.problems.map((p) => (
                  <li key={p} className="break-all">
                    {p}
                  </li>
                ))}
              </ul>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => setOutcome(null)}>
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}
